import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  reminderPresets,
  unitSuffix,
  REMINDER_PRESET_NAMES,
  type ReminderPrefs,
  type ReminderPresetName,
  type UnitSystem,
} from '@cartrack/shared';
import { api } from './api';

export type { ReminderPrefs, ReminderPresetName };

export interface Me {
  id: string;
  email: string;
  role: 'USER' | 'ADMIN';
  plan: 'free' | 'premium' | 'fleet';
  unitSystem: UnitSystem;
  displayName: string | null;
  country: string;
  stateProvince: string | null;
  postalCode: string | null;
  reminders: ReminderPrefs;
}

/** Shape accepted by `PATCH /users/me` — mirrors `api.updateMe`'s parameter. */
export type UpdateMeDto = Parameters<typeof api.updateMe>[0];

/**
 * Identify the active preset. The unit system is REQUIRED: the presets differ only in
 * their distance lead, so comparing a metric user's prefs against imperial numbers
 * reports every one of them as "custom".
 */
export function detectReminderPreset(
  prefs: ReminderPrefs,
  unit: UnitSystem,
): ReminderPresetName | 'custom' {
  const presets = reminderPresets(unit);
  for (const name of REMINDER_PRESET_NAMES) {
    const shape = presets[name];
    if (
      prefs.leadDays === shape.leadDays &&
      prefs.repeatWeeks === shape.repeatWeeks &&
      prefs.followUpDays === shape.followUpDays &&
      prefs.followUpEnabled === shape.followUpEnabled &&
      prefs.leadMiles === shape.leadMiles
    ) {
      return name;
    }
  }
  return 'custom';
}

export function presetDescriptions(unit: UnitSystem): Record<ReminderPresetName, string> {
  const presets = reminderPresets(unit);
  const s = unitSuffix(unit);
  const { relaxed, standard, tight } = presets;
  return {
    relaxed: `Start reminders ${relaxed.leadDays} days or ${relaxed.leadMiles.toLocaleString()} ${s} out, repeat every ${relaxed.repeatWeeks} weeks.`,
    standard: `Start reminders ${standard.leadDays} days or ${standard.leadMiles.toLocaleString()} ${s} out, repeat every ${standard.repeatWeeks} weeks. (Recommended.)`,
    tight: `Start reminders ${tight.leadDays} days or ${tight.leadMiles.toLocaleString()} ${s} out, repeat every week.`,
  };
}

export function useMe(): { me: Me | undefined; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['me'],
    queryFn: api.getMe,
    staleTime: 5 * 60 * 1000,
  });
  return { me: q.data, isLoading: q.isLoading };
}

export function useUnitSystem(): UnitSystem {
  const { me } = useMe();
  return me?.unitSystem ?? 'metric';
}

export { unitSuffix };

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateMeDto) => api.updateMe(dto),
    onSuccess: (updated) => {
      qc.setQueryData(['me'], updated);
      // Unit change affects all schedules on the screen.
      qc.invalidateQueries({ queryKey: ['schedule'] });
    },
  });
}
